import { db, functions } from '../firebase';
import { doc, collection, addDoc, setDoc, increment, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth } from '../firebase';
import { snapshotMigrationConfig } from './migrationConfig';
import { trasPrimerPintado } from './trasPrimerPintado';
import { recordScreenView, recordSearch } from '../api/audit';
import {
  recordProfileView as apiRecordProfileView,
  recordProfileClick as apiRecordProfileClick,
} from '../api/profileMetrics';

/**
 * ¿La analítica vive ya en Postgres? Ante cualquier duda, NO.
 *
 * Un fallo al leer el interruptor no puede llevarse por delante un contador: se
 * cae al camino de siempre, que es el que hoy funciona.
 */
async function porApiAudit() {
  try {
    return (await snapshotMigrationConfig()).readsFrom('audit') === 'postgres';
  } catch (_) {
    return false;
  }
}

// ── LOS DOS CONTADORES AGREGADOS YA TIENEN CAMINO NUEVO ─────────────────────
// Aquí decía que el dominio `audit` del API era de SOLO LECTURA y que por eso no
// había nada que bifurcar. La ingesta se abrió en el API primero, como pedía
// esta nota (`api/src/services/auditWrite.ts`), y ahora `trackScreenView` y
// `trackSearch` van por el interruptor.
//
// Lo que la ingesta nueva arregla, y no es cosmético:
//   · El techo real del documento único de Firestore NO es 1 MB: es ~1 escritura
//     por segundo POR DOCUMENTO, compartida por TODA la base de usuarios. En
//     Postgres el bloqueo es por (día, pantalla) y la suma la hace la base con
//     `views = views + 1`, que es atómico: dos personas a la vez suman dos.
//   · El contador de búsquedas guarda el término CRUDO además del normalizado, y
//     una fila por búsqueda en vez de un número. Es lo único que permite saber
//     cuántas PERSONAS distintas buscaron algo.
//
// ⚠️ LO QUE SIGUE SIN CAMINO NUEVO, y es distinto: `profileEvents` y
// `analyticsEvents`. Con `audit` en Postgres esas dos escrituras siguen cayendo
// en Firestore, así que la gráfica de actividad (lo que el panel llama DAU/MAU)
// se queda plana. No se cierra aquí porque `profile_event` tiene una forma
// propia (tipo, objetivo, sesión, ciudad) y sus contadores los llevan callables
// del servidor: es una tanda aparte, no una línea.
// ⚠️ `is_backfill` NUNCA lo escribe la app: esa marca es de la fila del volcado
// histórico, y si la ingesta en vivo pudiera ponerla el panel dejaría de poder
// distinguir la serie real del volcado.

// ── Contadores del perfil público: los cuenta el SERVIDOR ────────────────────
// Aquí se incrementaban `profileViews` y los cuatro `*Clicks` con un updateDoc
// sobre el documento de OTRO (workshops/{id} o users/{mecanico}) desde el
// navegador del VISITANTE. Las reglas lo deniegan SIEMPRE —denylist B-6 de
// `workshops` (firestore.rules:625) y allowlist de `users`, que sólo admite al
// propio dueño— y el error se tragaba en un `.catch(() => {})`. Resultado real
// en producción: `mechanicProfile.profileViews`, que es el "N vistas" que
// MechanicProfile.js:382 le enseña al mecánico, NUNCA subió; los cuatro
// contadores de clic tampoco.
// Los cuentan las callables `recordProfileView` / `recordProfileClick`
// (functions/src/misc.js), que excluyen al dueño y deduplican por
// visitante/perfil/día contra `profileViewLogs`. Esa deduplicación es también lo
// que hace inofensivo que WorkshopProfile.js:82 llame a `recordProfileView` por
// su cuenta: la segunda llamada del mismo día no cuenta dos veces.
// NO reponer el updateDoc: volvería a fallar en silencio.

// Identificador del perfil tal como lo esperan las callables: se manda
// EXACTAMENTE uno de los dos.
function profileTarget(targetType, targetId) {
  return targetType === 'workshop' ? { workshopId: targetId } : { mechanicId: targetId };
}

/**
 * ¿QUIÉN CUENTA ESTE PERFIL, la callable o el API?
 *
 * 🔴 EL DOMINIO DEPENDE DEL OBJETIVO, no de la pantalla: el contador del taller
 * vive en `workshops` y el del mecánico independiente dentro de
 * `users/{uid}.mechanicProfile`. Es la misma decisión que toma
 * `profileCounterDomain` en `functions/src/misc.js`, y tiene que coincidir: si
 * aquí se preguntara siempre por un solo dominio, uno de los dos contadores se
 * iría a la base que ya nadie pinta.
 *
 * Ante cualquier duda, NO: un fallo al leer el interruptor cae al camino de
 * siempre, que es el que hoy funciona.
 */
async function porApiPerfil(targetType) {
  try {
    const dominio = targetType === 'workshop' ? 'workshops' : 'users';
    return (await snapshotMigrationConfig()).readsFrom(dominio) === 'postgres';
  } catch (_) {
    return false;
  }
}

// Tipos de clic que tienen contador en el servidor. `product_click` no lo tiene
// (la callable lo rechaza con invalid-argument), así que sólo se guarda como
// evento suelto en `profileEvents`.
const COUNTED_CLICKS = ['whatsapp_click', 'call_click', 'message_click', 'appointment_click'];

// Genera o recupera un ID de sesión único (no es el uid del usuario)
function getSessionId() {
  let sid = sessionStorage.getItem('mf_session');
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('mf_session', sid);
  }
  return sid;
}

// Evita contar la misma vista más de una vez por sesión
function alreadyViewed(targetId) {
  return sessionStorage.getItem(`viewed_${targetId}`) === '1';
}
function markViewed(targetId) {
  sessionStorage.setItem(`viewed_${targetId}`, '1');
}

/**
 * Registra una vista de perfil (deduplicada por sesión).
 * No cuenta si el visitante es el dueño del perfil.
 */
export async function trackProfileView({ targetId, targetType, targetName, city = null }) {
  try {
    // Sin sesión no se cuenta: el evento y el contador exigen auth de todos
    // modos, así que llamar sin usuario solo produce un 401 en la consola del
    // visitante (medido en producción 2026-08-17 en la ficha pública). La
    // guardia vive AQUÍ, en la fuente, para que ningún llamador presente o
    // futuro repita el fallo por su cuenta.
    if (typeof auth.authStateReady === 'function') { try { await auth.authStateReady(); } catch (_) {} }
    if (!auth.currentUser) return;
    if (auth.currentUser?.uid === targetId) return; // dueño viendo su propio perfil
    if (alreadyViewed(targetId)) return;             // ya contada esta sesión
    markViewed(targetId);

    const event = {
      type: 'profile_view',
      targetId,
      targetType,
      targetName: targetName || '',
      viewerId: auth.currentUser?.uid || null,
      sessionId: getSessionId(),
      city,
      productId: null,
      createdAt: serverTimestamp(),
    };

    // Evento suelto (analítica del admin) y contador del perfil, en paralelo.
    // El contador lo cuenta el servidor — ver la nota del encabezado.
    const contar = (await porApiPerfil(targetType))
      ? apiRecordProfileView(targetType, targetId)
      : httpsCallable(functions, 'recordProfileView')(profileTarget(targetType, targetId));

    await Promise.all([
      addDoc(collection(db, 'profileEvents'), event),
      contar.catch(() => {}), // contar una visita nunca puede romper la pantalla
    ]);
  } catch (_) {
    // analytics nunca debe romper la UI
  }
}

/**
 * Registra un evento de conversión (signup, appointment_booked, checkout_completed, etc.)
 */
export async function trackEvent(type, props = {}) {
  try {
    await addDoc(collection(db, 'analyticsEvents'), {
      type,
      userId: auth.currentUser?.uid || null,
      sessionId: getSessionId(),
      ...props,
      createdAt: serverTimestamp(),
    });
  } catch (_) {
    // analytics nunca debe romper la UI
  }
}

/**
 * Registra un click de acción en el perfil.
 * type: 'whatsapp_click' | 'call_click' | 'message_click' | 'appointment_click' | 'product_click'
 */
export async function trackProfileClick({ type, targetId, targetType, targetName, city = null, productId = null }) {
  try {
    const event = {
      type,
      targetId,
      targetType,
      targetName: targetName || '',
      viewerId: auth.currentUser?.uid || null,
      sessionId: getSessionId(),
      city,
      productId,
      createdAt: serverTimestamp(),
    };

    let contar = Promise.resolve();
    if (COUNTED_CLICKS.includes(type)) {
      contar = (await porApiPerfil(targetType))
        ? apiRecordProfileClick(type, targetType, targetId)
        : httpsCallable(functions, 'recordProfileClick')({
            ...profileTarget(targetType, targetId),
            type,
          });
    }

    await Promise.all([
      addDoc(collection(db, 'profileEvents'), event),
      contar.catch(() => {}), // contar un clic nunca puede romper la pantalla
    ]);
  } catch (_) {
    // analytics nunca debe romper la UI
  }
}

// ── Analítica de comportamiento (contadores agregados, barata) ────────────────
// En vez de un doc por evento, se acumulan contadores en 2 docs fijos:
//   analytics/screens  → { views: { <pantalla>: n } }
//   analytics/searches → { terms: { <término>: n } }
// El costo es 1 escritura por incremento; las vistas se deduplican por sesión
// (una escritura por pantalla por sesión) para acotar el gasto.

// Limpia una clave para usarla como nombre de campo de un mapa Firestore
// (los nombres de campo no pueden contener . $ / [ ] ~ * ` #).
function sanitizeMapKey(s) {
  return String(s || '')
    .replace(/[.$/[\]~*`#]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Deriva una clave de pantalla legible desde el pathname, colapsando los
// segmentos que parecen IDs (id de Firestore, numéricos) a ":id".
export function screenKeyFromPath(pathname) {
  const parts = String(pathname || '').split('/').filter(Boolean);
  if (parts.length === 0) return 'landing';
  const clean = parts.map((p) => {
    if (/^\d+$/.test(p)) return ':id';
    if (p.length >= 15) return ':id';
    return p;
  });
  return sanitizeMapKey(clean.slice(0, 3).join('/'));
}

/** El envío, esperando a que haya algo en pantalla (ver `trasPrimerPintado`). */
function trasPintar(tarea) {
  return new Promise((resolver) => { trasPrimerPintado(() => resolver(tarea())); });
}

/**
 * Registra una vista de pantalla (contador agregado, deduplicado por sesión).
 * Recibe el pathname de la ruta; el llamador decide trackear solo clientes.
 *
 * 🔴 EL ENVÍO ESPERA AL PRIMER PINTADO, Y ESO ES EL ARREGLO. Medido contra
 * producción (móvil, 4G lenta): `POST /analytics/screen-views` salía EN EL MISMO
 * MILISEGUNDO que la petición del perfil del usuario y tardaba 682–725 ms
 * peleándose por la conexión. Es una escritura de analítica compitiendo con lo
 * que la persona está esperando ver.
 *
 * ⚠️ EL PRIMER INTENTO (`requestIdleCallback`) SE QUEDÓ A MEDIAS, y está medido:
 * dejó de salir empatada con el perfil (+0/+1 ms → +9 a +51 ms) y costó menos
 * (275–536 ms), pero seguía cayendo ENCIMA del primer pintado (−18 a +26 ms
 * respecto al FCP). «Hilo libre» no es «ya se ve algo». Ahora la señal es
 * `first-contentful-paint` (ver `src/utils/trasPrimerPintado.js`).
 *
 * Se difiere AQUÍ, en la fuente, y no en `App.js` (que la llama en cada cambio
 * de ruta) porque así ningún llamador presente o futuro tiene que acordarse. Solo
 * espera la PRIMERA: a partir del pintado, el búfer de `paint` ya trae la
 * entrada y las vistas siguientes salen en cuanto hay hueco.
 *
 * ⚠️ QUÉ SE PIERDE, dicho claro: la marca de sesión se pone AL PROGRAMAR (para
 * no encolar la misma pantalla dos veces), así que si la pestaña se cierra antes
 * de que llegue el pintado, esa vista no se cuenta y no se reintenta en esa
 * sesión. La ventana creció respecto al intento anterior, y aun así se acepta:
 * una pestaña que se cierra ANTES del primer pintado no llegó a enseñar la
 * pantalla, así que no contarla es más correcto, no menos. Navegar DENTRO de la
 * app no pierde nada — esto no cuelga de ningún componente, así que desmontar la
 * pantalla no cancela el envío.
 */
export async function trackScreenView(pathname) {
  try {
    if (!auth.currentUser) return;
    const key = screenKeyFromPath(pathname);
    if (!key) return;
    if (sessionStorage.getItem(`scr_${key}`) === '1') return; // ya contada esta sesión
    sessionStorage.setItem(`scr_${key}`, '1');
    return await trasPintar(() => enviarVistaDePantalla(key));
  } catch (_) {
    // analytics nunca debe romper la UI
  }
}

/** El envío en sí. Separado para que lo de arriba solo decida CUÁNDO. */
async function enviarVistaDePantalla(key) {
  try {
    if (await porApiAudit()) {
      // La deduplicación por sesión se queda ARRIBA a propósito: no es un
      // candado de seguridad sino un acotador de gasto, y el servidor no puede
      // saber si esta sesión ya contó sin guardar un registro por sesión, que es
      // justo lo que un contador agregado existe para no hacer.
      await recordScreenView(key);
      return;
    }
    await setDoc(
      doc(db, 'analytics', 'screens'),
      { views: { [key]: increment(1) }, updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (_) {
    // analytics nunca debe romper la UI
  }
}

/**
 * Registra un término de búsqueda (contador agregado). El llamador debe
 * hacer debounce (registrar solo cuando el usuario deja de escribir) y
 * trackear solo clientes.
 */
export async function trackSearch(term, surface = 'MARKETPLACE') {
  try {
    if (!auth.currentUser) return;
    const norm = String(term || '').toLowerCase().trim().slice(0, 40);
    if (norm.length < 2) return;
    const key = sanitizeMapKey(norm);
    if (!key) return;
    if (await porApiAudit()) {
      // 🔴 Se manda el término CRUDO, no `key`. `sanitizeMapKey` existe solo
      // porque un nombre de campo de Firestore no admite `. $ / [ ] ~ * ` #`, y
      // de paso colapsa "5.30" y "5 30" en la misma entrada: por ahí se pierde
      // para siempre justo el detalle que hace útil una búsqueda de repuestos.
      // Una columna de texto no tiene ese problema.
      await recordSearch({ term: String(term).trim().slice(0, 200), surface, sessionId: getSessionId() });
      return;
    }
    await setDoc(
      doc(db, 'analytics', 'searches'),
      { terms: { [key]: increment(1) }, updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (_) {
    // analytics nunca debe romper la UI
  }
}
